import { ReactNode, useEffect, useState } from "react";
import { useGestaoCobrancaCadastroContext } from "../../../context/gestaoCobranca/GestaoCobrancaCadastroContext";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCalendarAlt } from "react-icons/fa";
import CustomButton from "shared/components/button/CustomButton";
import HeaderPanel from "shared/components/panel/HeaderPanel";
import { Panel } from "shared/components/panel/Panel";
import BodyPanel from "shared/components/panel/BodyPanel";
import CustomAccordion from "shared/components/accordion/CustomAccordion";
import { CustomAccordionItem } from "types/accordion/CustomAccordionItemType";
import TabelaCobrancasEmAberto from "./TabelaCobrancasEmAberto";
import TabelaAcordosRealizados from "./TabelaAcordosRealizados";
import FooterPanel from "shared/components/panel/FooterPanel";
import SelectUnidades from "shared/components/select/selectUnidades/SelectUnidades";
import { InformacoesUnidade } from "./InformacoesUnidade";
import { Collapse } from "@mui/material";
import IntegracaoV1 from "shared/util/integrarFrontEndV1Functions";
import usePostAlterarCondominioContextoToken from "hooks/condominio/usePostAlterarCondominioContextoToken";
import ModalAgendamento from "./ModalAgendamento";
import { usePermissoesContext } from "context/permissoes/PermissoesContext";
import { TipoPermissao } from "constants/EnumTipoPermissao";
import { useGetIdsInadimplenciasIniciarAcordo } from "hooks/contaReceber/useGetIdsInadimplenciasIniciarAcordo";

export const GestaoCobrancaCadastro = () => {
  const { iniciarAcordosV1 } = IntegracaoV1();
  const navigate = useNavigate();
  const dadosCondominio = useLocation().state;

  const [showModalAgendamento, setShowModalAgendamento] = useState<boolean>(false);
  const [mostrarConteudo, setMostrarConteudo] = useState<boolean>(false);
  const [deveBuscarIdsInadimplencias, setDeveBuscarIdsInadimplencias] = useState<boolean>(false);
  const {
    inadimplenciasSelecionadas,
    inadimplencias,
    unidadeId,
    condominioId,
    possuiPermissaoIniciarAcordo,
    setUnidadeId,
    setCondominioId,
    opcaoSelecionadaCheckBox,
  } = useGestaoCobrancaCadastroContext();
  const { alterarCondominioContextoToken } = usePostAlterarCondominioContextoToken();
  const { usuarioPossuiPermissao } = usePermissoesContext();
  const {} = useGetIdsInadimplenciasIniciarAcordo({
    condominioId: condominioId,
    unidadeId,
    deveBuscarIdsInadimplencias,
  });

  useEffect(() => {
    setCondominioId(dadosCondominio.condominioId);
  }, []);

  const itensAccordionAcordo: CustomAccordionItem[] = [
    {
      key: "acordos-realizados",
      titulo: "Acordos realizados",
      conteudo: <TabelaAcordosRealizados />,
    },
  ];

  const itensAccordionInadimplencia: CustomAccordionItem[] = [
    {
      key: "inadimplencias",
      titulo: "Cobranças em aberto",
      conteudo: <TabelaCobrancasEmAberto />,
      botoes: possuiPermissaoIniciarAcordo
        ? [
            <CustomButton
              id="btn-iniciar-acordo"
              key="btn-iniciar-acordo"
              label="Iniciar acordo"
              variant="outline-primary"
              disabled={
                (inadimplencias && inadimplencias.length === 0) ||
                (inadimplenciasSelecionadas.length === 0 && opcaoSelecionadaCheckBox !== "TODAS_FILTRO")
              }
              onClick={(event) => {
                event.stopPropagation();
                iniciarAcordos(inadimplenciasSelecionadas);
              }}
            />,
          ]
        : [],
    },
  ];

  const iniciarAcordos = async (idsContasReceber: number[]) => {
    if (opcaoSelecionadaCheckBox === "TODAS_FILTRO") {
      setDeveBuscarIdsInadimplencias(true);
    } else if (idsContasReceber.length > 0 && condominioId) {
      await alterarCondominioContextoToken(condominioId);
      iniciarAcordosV1(idsContasReceber);
    }
  };

  const selecionarUnidade = (unidadeId: number) => {
    setUnidadeId(unidadeId);
    if (!mostrarConteudo) {
      setMostrarConteudo(true);
    }
  };

  const renderBotoesHeaderPanel = (): ReactNode[] => {
    let botoes = [];
    const possuiPermissaoIncluirAgendamento = usuarioPossuiPermissao("agendamento", TipoPermissao.INCLUIR);
    if (unidadeId && possuiPermissaoIncluirAgendamento) {
      botoes.push(
        <CustomButton
          key={"btnAgendamento"}
          variant="primary"
          className="e-5"
          id="btnAgendamento"
          label="Agendamento"
          leftIcon={FaCalendarAlt}
          onClick={() => setShowModalAgendamento(true)}
        />
      );
    }
    return botoes;
  };

  return (
    <Panel
      headerPanel={
        <HeaderPanel
          title="Gestão de Cobranças"
          nomeCondominio={dadosCondominio?.nomeCondominio}
          botoes={renderBotoesHeaderPanel()}
        />
      }
      footerPanel={
        <FooterPanel
          title="FooterPanel"
          botoes={[
            <CustomButton
              key={"btnVoltar"}
              variant="danger-alt"
              id="btnVoltar"
              label="Voltar"
              size="sm"
              className="footer-panel-btn"
              onClick={() => navigate(-1)}
            />,
          ]}
        />
      }
    >
      <BodyPanel>
        <SelectUnidades
          label="Selecione a unidade*"
          condominioId={condominioId}
          invalidateQuerysKey={["acordos-realizados"]}
          onChange={(selected) => selecionarUnidade(selected?.value)}
        />
        <ModalAgendamento showModal={showModalAgendamento} onCloseModal={() => setShowModalAgendamento(false)} />
      </BodyPanel>

      <Collapse in={mostrarConteudo} timeout={1000}>
        <div>
          <BodyPanel>
            <InformacoesUnidade />
            <CustomAccordion keyItemAtivoPorPadrao="inadimplencias" itens={itensAccordionInadimplencia} />
            <CustomAccordion keyItemAtivoPorPadrao="acordos-realizados" itens={itensAccordionAcordo} />
          </BodyPanel>
        </div>
      </Collapse>
    </Panel>
  );
};
